import React from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';

/**
 * How many zones one credit customer's ONE rate card has, what they are called, and
 * which states sit in each.
 *
 * Zones were four, globally, for everybody; then four, per customer. They are now per
 * customer AND per card, with a configurable count — because a customer's Fixed and
 * Bulk cards are separate agreements that need share neither their grouping nor their
 * number of zones.
 *
 * The rest of rate management is untouched: the cards are still uploaded, templated
 * and exported the same way. What changes is how many zone columns they carry and what
 * each one means.
 *
 * The screen's job beyond editing is to make one consequence impossible to miss:
 * adding a zone does not add prices, and a shipment to a zone with no price is REFUSED
 * at the counter rather than billed at zero. So the coverage strip says, up front,
 * which zones have nothing to charge.
 */

const RATE_TYPES = [
    { key: 'fixed', label: 'Fixed Rate' },
    { key: 'bulk', label: 'Bulk Rate' },
    { key: 'flat', label: 'Flat Rate' },
    { key: 'multiplier', label: 'Multiplier Rate' },
    { key: 'special', label: 'Special Rate' },
];

type ZoneRow = { zone_number: number; label: string | null; display?: string };

type StateRow = {
    state_name: string;
    state_key: string;
    zone_number: number;
    default_zone_number: number;
    is_custom: boolean;
    is_stranded?: boolean;
};

type Coverage = { zone_number: number; total_rows: number; missing: number; priceable: boolean };

const ZONE_TINT: Record<number, string> = {
    1: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    2: 'bg-sky-50 border-sky-200 text-sky-900',
    3: 'bg-amber-50 border-amber-200 text-amber-900',
    4: 'bg-rose-50 border-rose-200 text-rose-900',
    5: 'bg-violet-50 border-violet-200 text-violet-900',
    6: 'bg-teal-50 border-teal-200 text-teal-900',
    7: 'bg-orange-50 border-orange-200 text-orange-900',
    8: 'bg-lime-50 border-lime-200 text-lime-900',
    9: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900',
    10: 'bg-slate-100 border-slate-300 text-slate-900',
};

const tint = (n: number) => ZONE_TINT[n] || 'bg-gray-50 border-gray-200 text-gray-900';

export default function CreditZoneConfig({ clientEmail }: { clientEmail: string }) {
    const [rateType, setRateType] = React.useState<string>('fixed');

    const [loading, setLoading] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [zones, setZones] = React.useState<ZoneRow[]>([]);
    const [rows, setRows] = React.useState<StateRow[]>([]);
    const [orphans, setOrphans] = React.useState<any[]>([]);
    const [coverage, setCoverage] = React.useState<Coverage[]>([]);
    const [isCustomised, setIsCustomised] = React.useState<boolean>(false);
    const [maxZones, setMaxZones] = React.useState<number>(10);
    const [search, setSearch] = React.useState<string>('');
    const [confirmReset, setConfirmReset] = React.useState<boolean>(false);
    const [removing, setRemoving] = React.useState<ZoneRow | null>(null);
    const [moveTo, setMoveTo] = React.useState<number>(1);

    // What the server last returned, so the dirty marker and "Discard" need no refetch.
    const [saved, setSaved] = React.useState<string>('');

    const snapshot = (z: ZoneRow[], r: StateRow[]) =>
        JSON.stringify({
            z: z.map((x) => [x.zone_number, x.label || '']),
            r: r.map((x) => [x.state_key, x.zone_number]),
        });

    const load = React.useCallback(() => {
        if (!clientEmail) return;

        setLoading(true);
        SuperAdminApis.getCreditCustomerZones(clientEmail, rateType)
            .then((res: any) => {
                const d = res?.data?.data;
                if (!res?.data?.status || !d) {
                    toast.error(res?.data?.message || 'Could not load the zone configuration');
                    return;
                }
                const z = (d.zones || []).map((x: any) => ({ ...x, label: x.label ?? null }));
                setZones(z);
                setRows(d.states || []);
                setOrphans(d.orphans || []);
                setCoverage(d.coverage || []);
                setIsCustomised(!!d.is_customised);
                setMaxZones(d.max_zones || 10);
                setSaved(snapshot(z, d.states || []));
            })
            .catch((err: any) => toast.error(err?.response?.data?.message || 'Could not load the zone configuration'))
            .finally(() => setLoading(false));
    }, [clientEmail, rateType]);

    React.useEffect(() => { load(); }, [load]);

    const dirty = snapshot(zones, rows) !== saved;

    const movedFromDefault = React.useMemo(
        () => rows.filter((r) => r.zone_number !== r.default_zone_number),
        [rows]
    );

    const visible = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? rows.filter((r) => r.state_name.toLowerCase().includes(q)) : rows;
    }, [rows, search]);

    const addZone = () => {
        if (zones.length >= maxZones) return;
        setZones([...zones, { zone_number: zones.length + 1, label: null }]);
    };

    /** Removing the top zone re-homes whatever was in it — the card cannot keep a state
     *  pointing at a zone that no longer exists, and the backend refuses to save one. */
    const confirmRemove = () => {
        if (!removing) return;
        const gone = removing.zone_number;
        setZones(zones.filter((z) => z.zone_number !== gone));
        setRows(rows.map((r) => (r.zone_number === gone ? { ...r, zone_number: moveTo } : r)));
        setRemoving(null);
    };

    const stateCount = (n: number) => rows.filter((r) => r.zone_number === n).length;

    const save = () => {
        setSaving(true);
        SuperAdminApis.saveCreditCustomerZones(
            clientEmail,
            rateType,
            zones.map((z) => ({ zone_number: z.zone_number, label: z.label || null })),
            rows.map((r) => ({ state_name: r.state_name, zone_number: r.zone_number }))
        )
            .then((res: any) => {
                if (!res?.data?.status) {
                    toast.error(res?.data?.message || 'Could not save the zone configuration');
                    return;
                }
                toast.success(res.data.message);
                load();
            })
            .catch((err: any) => toast.error(err?.response?.data?.message || 'Could not save the zone configuration'))
            .finally(() => setSaving(false));
    };

    const reset = () => {
        setSaving(true);
        SuperAdminApis.resetCreditCustomerZones(clientEmail, rateType)
            .then((res: any) => {
                if (!res?.data?.status) {
                    toast.error(res?.data?.message || 'Could not reset the zone configuration');
                    return;
                }
                toast.success(res.data.message);
                setConfirmReset(false);
                load();
            })
            .catch((err: any) => toast.error(err?.response?.data?.message || 'Could not reset the zone configuration'))
            .finally(() => setSaving(false));
    };

    if (!clientEmail) {
        return <div className="bg-white rounded-lg p-6 text-sm text-gray-500">Choose a credit customer first.</div>;
    }

    const currentLabel = RATE_TYPES.find((t) => t.key === rateType)?.label || rateType;
    const gaps = coverage.filter((c) => c.total_rows > 0 && c.missing > 0);

    return (
        <div>
            {/* ── Which card are we configuring ───────────────── */}
            <div className="bg-white rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-1">Zone configuration</h4>
                <p className="text-sm text-gray-600 mb-3">
                    Each rate card has its own zones. Pick the card you want to configure.
                </p>
                <div className="flex flex-wrap gap-2">
                    {RATE_TYPES.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => { if (!dirty || window.confirm('Discard unsaved changes to this card?')) setRateType(t.key); }}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${rateType === t.key
                                ? 'bg-gray-800 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="font-semibold text-blue-900">{currentLabel} zones for {clientEmail}</p>
                <p className="text-sm text-blue-800 mt-2">
                    This decides which states fall in each zone <strong>on this card only</strong>. The other
                    rate cards keep their own zones. Uploading, templates and exports are unchanged — they
                    simply carry however many zone columns this card has.
                </p>
                <p className="text-sm text-blue-800 mt-2">
                    {isCustomised
                        ? 'This card is on its own zone map. Changes to the general zone setup will no longer reach it.'
                        : 'This card follows the general zone setup. Nothing changes until you save a configuration here.'}
                </p>
            </div>

            {/* ── Status + actions ───────────────────────────── */}
            <div className="bg-white rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isCustomised ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                        {isCustomised ? 'Custom zone map' : 'Using the default zone map'}
                    </span>
                    <span className="text-xs text-gray-600">{zones.length} zone{zones.length === 1 ? '' : 's'}</span>
                    {movedFromDefault.length > 0 && (
                        <span className="text-xs text-gray-600">
                            {movedFromDefault.length} state{movedFromDefault.length === 1 ? '' : 's'} off the default
                        </span>
                    )}
                    {dirty && <span className="text-xs text-red-600 font-medium">unsaved changes</span>}
                </div>

                <div className="flex items-center gap-2">
                    {dirty && (
                        <button onClick={load} disabled={saving}
                            className="px-3 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300">
                            Discard changes
                        </button>
                    )}
                    {isCustomised && (
                        <button onClick={() => setConfirmReset(true)} disabled={saving}
                            className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                            Reset to default
                        </button>
                    )}
                    <button onClick={save} disabled={saving || loading || !dirty}
                        className="px-4 py-2 text-sm rounded-md bg-[#E04343] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                        {saving ? 'Saving…' : 'Save zone configuration'}
                    </button>
                </div>
            </div>

            {/* Adding a zone does not add prices to the card. A shipment to a zone with
                no price is refused at the counter — said here so it is never a surprise. */}
            {gaps.length > 0 && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                    <p className="font-semibold text-amber-900">
                        {gaps.length === 1 ? 'One zone has no prices on this card' : `${gaps.length} zones have no prices on this card`}
                    </p>
                    <ul className="text-sm text-amber-800 mt-2 ml-4 list-disc">
                        {gaps.map((c) => (
                            <li key={c.zone_number}>
                                <strong>Zone {c.zone_number}</strong> — {c.missing} of {c.total_rows} rows have no rate
                            </li>
                        ))}
                    </ul>
                    <p className="text-sm text-amber-800 mt-2">
                        Bookings to states in those zones will be <strong>refused</strong> until the prices are added
                        on the {currentLabel} tab. Nothing is billed at ₦0.
                    </p>
                </div>
            )}

            {orphans.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800">
                    {orphans.length} configured state{orphans.length === 1 ? '' : 's'} no longer exist in the general
                    zone setup ({orphans.map((o: any) => o.state_key).join(', ')}) and will never be matched.
                    Saving this screen will clear them.
                </div>
            )}

            {/* ── The zones ──────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3">
                {zones.map((z) => {
                    const cover = coverage.find((c) => c.zone_number === z.zone_number);
                    const empty = cover && cover.total_rows > 0 && cover.missing === cover.total_rows;

                    return (
                        <div key={z.zone_number} className={`rounded-lg border p-3 ${tint(z.zone_number)}`}>
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="font-semibold text-sm">Zone {z.zone_number}</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs opacity-75">{stateCount(z.zone_number)} state{stateCount(z.zone_number) === 1 ? '' : 's'}</span>
                                    {/* Only the top zone is removable, so the numbering stays 1..N with no gaps. */}
                                    {z.zone_number === zones.length && zones.length > 1 && (
                                        <button
                                            onClick={() => { setRemoving(z); setMoveTo(1); }}
                                            title={`Remove Zone ${z.zone_number}`}
                                            className="text-xs opacity-60 hover:opacity-100"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <input
                                value={z.label || ''}
                                onChange={(e) => setZones(zones.map((x) => x.zone_number === z.zone_number ? { ...x, label: e.target.value || null } : x))}
                                placeholder="Name this zone (optional)"
                                className="w-full mt-2 text-xs bg-white/70 border border-white rounded px-2 py-1"
                            />

                            <p className="text-xs mt-2 leading-relaxed">
                                {stateCount(z.zone_number) === 0
                                    ? <span className="opacity-60">No states yet.</span>
                                    : rows.filter((r) => r.zone_number === z.zone_number).map((m) => m.state_name).join(', ')}
                            </p>

                            {empty && <p className="text-[11px] mt-2 font-medium opacity-80">No prices on the card for this zone.</p>}
                        </div>
                    );
                })}

                {zones.length < maxZones && (
                    <button
                        onClick={addZone}
                        className="rounded-lg border-2 border-dashed border-gray-300 p-3 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 min-h-[96px]"
                    >
                        + Add Zone {zones.length + 1}
                    </button>
                )}
            </div>

            <p className="text-xs text-gray-500 mb-6">
                Up to {maxZones} zones. A new zone starts empty — move states into it below, then add its
                prices on the {currentLabel} tab.
            </p>

            {/* ── The map ────────────────────────────────────── */}
            <div className="bg-white rounded-lg">
                <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
                    <h4 className="font-semibold">States</h4>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Find a state…"
                        className="border rounded-md px-3 py-2 text-sm w-full sm:w-64"
                    />
                </div>

                {loading ? (
                    <p className="p-6 text-sm text-gray-500 text-center">Loading…</p>
                ) : visible.length === 0 ? (
                    <p className="p-6 text-sm text-gray-500 text-center">No state matches “{search}”.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                                <tr>
                                    <th className="px-4 py-2 font-medium">State</th>
                                    <th className="px-4 py-2 font-medium">Zone on this card</th>
                                    <th className="px-4 py-2 font-medium">Default</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => {
                                    const offDefault = r.zone_number !== r.default_zone_number;

                                    return (
                                        <tr key={r.state_key} className="border-b last:border-b-0">
                                            <td className="px-4 py-2 font-medium whitespace-nowrap">
                                                {r.state_name}
                                                {offDefault && (
                                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                                                        moved
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="flex gap-1 flex-wrap">
                                                    {zones.map((z) => (
                                                        <button
                                                            key={z.zone_number}
                                                            onClick={() => setRows(rows.map((x) => x.state_key === r.state_key ? { ...x, zone_number: z.zone_number } : x))}
                                                            title={z.label || `Zone ${z.zone_number}`}
                                                            className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${r.zone_number === z.zone_number
                                                                ? tint(z.zone_number) + ' font-semibold'
                                                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {z.zone_number}
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                                                Zone {r.default_zone_number}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Removing a zone: say where its states go ───── */}
            {removing && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">Remove Zone {removing.zone_number}?</h3>
                            <button onClick={() => setRemoving(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>
                        <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
                            {stateCount(removing.zone_number) === 0 ? (
                                <p>Zone {removing.zone_number} has no states in it, so nothing moves.</p>
                            ) : (
                                <>
                                    <p>
                                        {stateCount(removing.zone_number)} state(s) are in Zone {removing.zone_number} and
                                        need somewhere to go:
                                    </p>
                                    <p className="text-xs bg-gray-50 border rounded px-3 py-2">
                                        {rows.filter((r) => r.zone_number === removing.zone_number).map((r) => r.state_name).join(', ')}
                                    </p>
                                    <label className="block text-xs font-medium text-gray-600">Move them to</label>
                                    <select
                                        value={moveTo}
                                        onChange={(e) => setMoveTo(Number(e.target.value))}
                                        className="w-full border rounded-md px-3 py-2 text-sm"
                                    >
                                        {zones.filter((z) => z.zone_number !== removing.zone_number).map((z) => (
                                            <option key={z.zone_number} value={z.zone_number}>
                                                Zone {z.zone_number}{z.label ? ` — ${z.label}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}
                            <p className="text-xs text-gray-500">
                                The prices already in the Zone {removing.zone_number} column stay on the card until you
                                change them, but nothing will be priced against them.
                            </p>
                        </div>
                        <div className="px-5 py-3 border-t flex justify-end gap-2">
                            <button onClick={() => setRemoving(null)} className="px-3 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300">Cancel</button>
                            <button onClick={confirmRemove} className="px-4 py-2 text-sm rounded-md bg-[#E04343] text-white font-medium">Remove zone</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirm reset ──────────────────────────────── */}
            {confirmReset && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">Reset the {currentLabel} zones?</h3>
                            <button onClick={() => setConfirmReset(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>
                        <div className="px-5 py-4 text-sm text-gray-700 space-y-3">
                            <p>
                                This card goes back to the default four zones and the general zone setup, and will
                                follow any later changes to it. The other rate cards are not affected.
                            </p>
                            {movedFromDefault.length > 0 && (
                                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs">
                                    {movedFromDefault.length} state(s) currently priced off the default will move back.
                                    New shipments to those states will be priced differently.
                                </p>
                            )}
                            <p className="text-xs text-gray-500">Rate cards, uploads and past invoices are not affected.</p>
                        </div>
                        <div className="px-5 py-3 border-t flex justify-end gap-2">
                            <button onClick={() => setConfirmReset(false)} className="px-3 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300">Cancel</button>
                            <button onClick={reset} disabled={saving} className="px-4 py-2 text-sm rounded-md bg-[#E04343] text-white font-medium disabled:opacity-50">
                                {saving ? 'Resetting…' : 'Reset to default'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
